import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PendingVerifications.css";

const API_ROOT          = (process.env.REACT_APP_API_URL          || "").replace(/\/$/, "");
const LEGACY_PORTAL_BASE = (process.env.REACT_APP_LEGACY_PORTAL_BASE || "http://127.0.0.1").replace(/\/$/, "");
const ALLOWED_MODULES   = [2, 4, 9, 15];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB");
}

function hfDayLabel(val) {
  if (val === "F") return "First Half";
  if (val === "S") return "Second Half";
  return "Full Day";
}

export default function LeaveDetailsOffice() {
  const navigate = useNavigate();

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid,   10);
  const empId    = parseInt(localUser?.id,        10);

  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // Module 4 (Leave Office) only: Select Employee / Leave Type filters + View button.
  const [empOptions,        setEmpOptions]        = useState([]);
  const [leaveTypeOptions,  setLeaveTypeOptions]   = useState([]);
  const [selectedEmpId,     setSelectedEmpId]      = useState("");
  const [selectedLeaveType, setSelectedLeaveType]  = useState("");
  const [hasSearched,       setHasSearched]        = useState(moduleId !== 4);

  const fetchList = async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ moduleId });
      if ([2, 15].includes(moduleId)) params.append("deptId", deptId);
      if (moduleId === 9)             params.append("empId",  empId);
      if (moduleId === 4) {
        if (overrides.empId)       params.append("empId",       overrides.empId);
        if (overrides.leaveTypeId) params.append("leaveTypeId",  overrides.leaveTypeId);
      }

      const res  = await fetch(`${API_ROOT}/api/employees/leave-details-office?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load leave records");
      setList(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || "Failed to load leave records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) return;

    if (moduleId === 4) {
      fetch(`${API_ROOT}/api/employees/shift/employee-options?moduleId=${moduleId}`)
        .then(r => r.json())
        .then(data => setEmpOptions(Array.isArray(data) ? data : []))
        .catch(() => {});
      fetch(`${API_ROOT}/api/employees/leave-types-office?moduleId=${moduleId}`)
        .then(r => r.json())
        .then(data => setLeaveTypeOptions(Array.isArray(data) ? data : []))
        .catch(() => {});
      return;
    }

    fetchList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const onView = (e) => {
    e.preventDefault();
    if (!selectedEmpId) { setError("Please select an Employee."); return; }
    setHasSearched(true);
    fetchList({ empId: selectedEmpId, leaveTypeId: selectedLeaveType });
  };

  const printLeaveOrder = (leaveId) => {
    const form  = document.createElement("form");
    form.method = "POST";
    form.action = `${LEGACY_PORTAL_BASE}/LeaveMgmt/AjaxLeaveOrderPrint.php`;
    form.target = "_blank";
    const input  = document.createElement("input");
    input.type   = "hidden";
    input.name   = "LeaveID";
    input.value  = leaveId;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const showDepttAbb = [4, 9].includes(moduleId);
  const showPrint    = moduleId === 9;

  if (!ALLOWED_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex align-items-center mb-3 gap-2">
        <h3 className="ctitle mb-0">Leave Details</h3>
        <button
          className="btn btn-primary btn-sm ms-3"
          onClick={() => navigate("/leave-by-office-new")}
        >
          New Leave
        </button>
      </div>

      {moduleId === 4 && (
        <form className="row g-2 align-items-end mb-3" onSubmit={onView}>
          <div className="col-sm-5">
            <label className="form-label mb-1">Select Employee</label>
            <select className="form-select form-select-sm" value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
              <option value="">-- Select Employee --</option>
              {empOptions.map(emp => (
                <option key={emp.EmployeeID} value={emp.EmployeeID}>
                  {emp.EmpName} — {emp.Department} — {emp.Designation}
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-3">
            <label className="form-label mb-1">Leave Type</label>
            <select className="form-select form-select-sm" value={selectedLeaveType} onChange={e => setSelectedLeaveType(e.target.value)}>
              <option value="">All Leave Types</option>
              {leaveTypeOptions.map(lt => (
                <option key={lt.LeaveTypeID} value={lt.LeaveTypeID}>{lt.LeaveType}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <button type="submit" className="btn btn-primary btn-sm form-control" disabled={loading}>
              {loading ? "Loading..." : "View"}
            </button>
          </div>
        </form>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!hasSearched ? null : loading ? (
        <div className="text-muted">Loading...</div>
      ) : list.length === 0 ? (
        <div className="alert alert-info">No leave records found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle pending-verif-table">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                {showDepttAbb && <th>Dept</th>}
                <th>Employee</th>
                <th>Mobile</th>
                <th>Entered By</th>
                <th>Leave Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Duration</th>
                <th className="remarks-cell">Remarks</th>
                <th>Status</th>
                {showPrint && <th>Print</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <tr key={row.LeaveID ?? idx}>
                  <td>{idx + 1}</td>
                  {showDepttAbb && <td>{row.DepttAbb}</td>}
                  <td>{row.EmpName}</td>
                  <td>{row.Mobile}</td>
                  <td>{row.EntryByName}</td>
                  <td>{row.LeaveType}</td>
                  <td>{formatDate(row.FromDate)}</td>
                  <td>{formatDate(row.ToDate)}</td>
                  <td>{row.NoOfDays}</td>
                  <td>{hfDayLabel(row.HFDay)}</td>
                  <td className="remarks-cell">{row.Remarks}</td>
                  <td>{row.Status}</td>
                  {showPrint && (
                    <td>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => printLeaveOrder(row.LeaveID)}
                      >
                        Print
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
